import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const ShareModal = ({ playbook, currentPlayId, onInvite, onRemove, onSetPublic, onClose }) => {
    const { currentUser } = useAuth();
    const [email, setEmail] = useState('');
    const [isInviting, setIsInviting] = useState(false);
    const [error, setError] = useState('');
    const [copiedPb, setCopiedPb] = useState(false);
    const [copiedPlay, setCopiedPlay] = useState(false);

    const collaborators = playbook?.collaborators || {};
    const collaboratorList = Object.entries(collaborators).map(([uid, data]) => ({
        uid,
        ...data
    }));

    const isPublic = playbook?.isPublic || false;
    const shareUrl = `${window.location.origin}/?share=${playbook?.id}`;
    const playUrl = currentPlayId ? `${window.location.origin}/?play=${playbook?.id}:${currentPlayId}` : null;

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;

        setIsInviting(true);
        setError('');
        try {
            await onInvite(email);
            setEmail('');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsInviting(false);
        }
    };

    const handleCopyPb = () => {
        navigator.clipboard.writeText(shareUrl);
        setCopiedPb(true);
        setTimeout(() => setCopiedPb(false), 2000);
    };

    const handleCopyPlay = () => {
        if (!playUrl) return;
        navigator.clipboard.writeText(playUrl);
        setCopiedPlay(true);
        setTimeout(() => setCopiedPlay(false), 2000);
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center bg-gray-900/50">
                    <div>
                        <h2 className="text-xl font-bold text-white">Share Playbook</h2>
                        <p className="text-xs text-gray-400 mt-0.5">{playbook?.name}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors text-2xl">&times;</button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Invite Section */}
                    <form onSubmit={handleInvite} className="space-y-4">
                        <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider">Invite Collaborator</label>
                        <div className="flex gap-2">
                            <input
                                autoFocus
                                type="email"
                                placeholder="coach@example.com"
                                className="flex-1 bg-gray-950 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 transition-colors"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <button
                                type="submit"
                                disabled={isInviting || !email.trim()}
                                className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold px-4 py-2 rounded-lg transition-all active:scale-95"
                            >
                                {isInviting ? '...' : 'Invite'}
                            </button>
                        </div>
                        {error && <p className="text-xs text-red-400 bg-red-400/10 p-2 rounded border border-red-400/20">{error}</p>}
                    </form>

                    {/* Collaborator List */}
                    <div className="space-y-4">
                        <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider">Current Collaborators</label>
                        <div className="space-y-2 max-h-42 overflow-y-auto pr-2 custom-scrollbar">
                            {collaboratorList.map(item => (
                                <div key={item.uid} className="flex items-center justify-between p-3 bg-gray-950/50 border border-gray-800/50 rounded-xl group hover:border-gray-700 transition-all">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400 font-bold text-xs uppercase">
                                            {item.displayName ? item.displayName.charAt(0) : '?'}
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-white leading-none mb-1">
                                                {item.displayName || 'Pending User'}
                                                {item.uid === currentUser?.uid && <span className="ml-2 text-[10px] bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded uppercase">You</span>}
                                            </p>
                                            <p className="text-xs text-gray-500">{item.email}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${item.role === 'owner' ? 'bg-amber-500/20 text-amber-500' : 'bg-green-500/20 text-green-500'}`}>
                                            {item.role}
                                        </span>
                                        {item.role !== 'owner' && item.uid !== currentUser?.uid && (
                                            <button
                                                onClick={() => onRemove(item.uid)}
                                                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-all text-xs font-bold"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Shared Link Toggle */}
                    <div className="pt-4 border-t border-gray-800">
                        <div className="p-4 bg-gray-800/30 border border-gray-700/50 rounded-xl space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex flex-col">
                                    <span className="text-sm font-bold">Public Share link</span>
                                    <span className="text-[10px] text-gray-500 uppercase font-black">Anyone with link can view</span>
                                </div>
                                <button
                                    onClick={() => onSetPublic(!isPublic)}
                                    className={`w-10 h-5 rounded-full relative transition-colors ${isPublic ? 'bg-blue-600' : 'bg-gray-700'}`}
                                >
                                    <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${isPublic ? 'left-6' : 'left-1'}`} />
                                </button>
                            </div>

                            {isPublic ? (
                                <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-tighter">Full Playbook Link</label>
                                        <div className="bg-gray-950 border border-gray-800 rounded px-3 py-2 text-[10px] font-mono text-gray-400 truncate">
                                            {shareUrl}
                                        </div>
                                        <button
                                            className={`w-full py-2 rounded-lg text-xs font-bold transition-all border ${copiedPb ? 'bg-green-600 border-green-500 text-white' : 'bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-700'}`}
                                            onClick={handleCopyPb}
                                        >
                                            {copiedPb ? 'Copied Full Link!' : 'Copy Playbook URL'}
                                        </button>
                                    </div>

                                    {currentPlayId && (
                                        <div className="space-y-2 pt-2 border-t border-gray-800/50">
                                            <label className="text-[10px] font-black text-gray-500 uppercase tracking-tighter">Direct Link to This Play</label>
                                            <div className="bg-gray-950 border border-gray-800 rounded px-3 py-2 text-[10px] font-mono text-gray-400 truncate">
                                                {playUrl}
                                            </div>
                                            <button
                                                className={`w-full py-2 rounded-lg text-xs font-bold transition-all border ${copiedPlay ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-700'}`}
                                                onClick={handleCopyPlay}
                                            >
                                                {copiedPlay ? 'Copied Play Link!' : 'Copy Link to This Play'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-[10px] text-gray-400 italic">
                                    Enable public sharing to generate view-only links.
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-950/50 border-t border-gray-800 flex justify-end">
                    <button
                        onClick={onClose}
                        className="text-sm font-bold text-gray-400 hover:text-white transition-colors"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ShareModal;
